// Export the runtime configuration with fallback
export const config = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};

// Helper function to get config values with type safety
export function getConfig(key) {
  if (typeof window === 'undefined') {
    console.warn('Window not available, cannot access runtime config');
    return '';
  }

  // Always get the latest config from window
  const currentConfig = window.__RUNTIME_CONFIG__;

  if (!currentConfig) {
    console.warn('Runtime configuration not loaded. Make sure config.js is loaded before this module.');
    return '';
  }

  const value = currentConfig[key];
  if (value === undefined) {
    console.warn(`Configuration key "${key}" not found in runtime config`);
    return '';
  }

  return value;
}

// Specific API URL getters
export function getApiGatewayUrl() {
  return getConfig('API_GATEWAY_URL');
}

export function getApiTimeout() {
  return getConfig('API_TIMEOUT') || 30000;
}

export function isLoggingEnabled() {
  return getConfig('ENABLE_LOGGING') === true;
}

export function getEnvironment() {
  return getConfig('ENVIRONMENT') || 'development';
}

// Branding logo getter (login / forgot-password). Pass the bundled asset as
// `fallback` so the logo still renders when config.js does not define a path/URL.
export function getLogoUrl(fallback = '') {
  const currentConfig = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  return currentConfig.LOGO_URL || fallback;
}

// Logo dimensions (any CSS length). Falls back to the given default when unset.
export function getLogoWidth(fallback = 'auto') {
  const currentConfig = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  return currentConfig.LOGO_WIDTH || fallback;
}

export function getLogoHeight(fallback = 'auto') {
  const currentConfig = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
  return currentConfig.LOGO_HEIGHT || fallback;
}

// Microservice URL getters
export function getAccountsApiUrl() {
  return getConfig('API_MS_ACCOUNTS_URL');
}

export function getAuthorizationApiUrl() {
  return getConfig('API_MS_AUTHORIZATION_URL');
}

export function getBillingApiUrl() {
  return getConfig('API_MS_BILLING_URL');
}

export function getCommunicationApiUrl() {
  return getConfig('API_MS_COMMUNICATION_URL');
}

export function getCrmApiUrl() {
  return getConfig('API_MS_CRM_URL');
}

export function getInventoryApiUrl() {
  return getConfig('API_MS_INVENTORY_URL');
}

export function getHospitalApiUrl() {
  return getConfig('API_MS_HOSPITAL_URL');
}

// HMS application base URL — used to launch HMS via SSO from the login
// dashboard. Value carries a trailing slash (see MS_HMS_URL in config.js).
export function getHmsAppUrl() {
  return getConfig('MS_HMS_URL') || '';
}

// Crystal Report server base URL (MoceroUI CrystalReportPrint WebForm).
export function getInventoryReportUrl() {
  return getConfig('INVENTORY_REPORT_URL') || '';
}

// Appointment booking app base URL (embedded as an iframe)
export function getAppointmentBaseUrl() {
  return getConfig('Appointment_BaseURL') || '';
}

// Utility function to get the appropriate API URL based on service type
export function getServiceApiUrl(serviceType) {
  const serviceMap = {
    'accounts': getAccountsApiUrl(),
    'authorization': getAuthorizationApiUrl(),
    'billing': getBillingApiUrl(),
    'communication': getCommunicationApiUrl(),
    'crm': getCrmApiUrl(),
    'inventory': getInventoryApiUrl(),
    'hospital': getHospitalApiUrl(),
    'gateway': getApiGatewayUrl()
  };

  return serviceMap[serviceType] || getApiGatewayUrl();
}

// Function to update runtime configuration (for admin purposes)
export function updateRuntimeConfig(newConfig) {
  if (typeof newConfig === 'object' && newConfig !== null) {
    Object.assign(window.__RUNTIME_CONFIG__, newConfig);
    console.log('Runtime configuration updated:', newConfig);
  }
}

// Function to get all configuration as a copy
export function getAllConfig() {
  return { ...config };
}
